import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import AdminLayout, { AdminPageHeader, AdminTable } from "./layout";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

interface Medication {
  id: number;
  name: string;
  genericName: string | null;
  drugClass: string;
  indication: string;
  mechanism: string | null;
  nurseNotes: string | null;
  patientNotes: string | null;
  monitoringRequirements: string;
  contraindications: string[];
  sideEffects: string[];
  bsrGuidance: string | null;
  pharmacokinetics: string | null;
  pharmacodynamics: string | null;
  bnfReference: string | null;
  patientSummary: string | null;
  patientMedicationGuide: string | null;
  createdAt: string;
}

type MedForm = Omit<Medication, "id" | "createdAt">;

const EMPTY: MedForm = {
  name: "",
  genericName: "",
  drugClass: "",
  indication: "",
  mechanism: "",
  nurseNotes: "",
  patientNotes: "",
  monitoringRequirements: "",
  contraindications: [],
  sideEffects: [],
  bsrGuidance: "",
  pharmacokinetics: "",
  pharmacodynamics: "",
  bnfReference: "",
  patientSummary: "",
  patientMedicationGuide: "",
};

async function apiFetch(path: string, opts?: RequestInit) {
  const res = await fetch(`${basePath}${path}`, { credentials: "include", ...opts });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? "Request failed");
  }
  return res.json();
}

function MedForm({ value, onChange }: { value: MedForm; onChange: (v: MedForm) => void }) {
  const set = (k: string, v: unknown) => onChange({ ...value, [k]: v });
  const arrStr = (a: string[]) => a.join(", ");
  const strArr = (s: string) => s.split(",").map((x) => x.trim()).filter(Boolean);

  return (
    <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Brand name *</Label>
          <Input value={value.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Methotrexate" />
        </div>
        <div>
          <Label>Generic name</Label>
          <Input value={value.genericName ?? ""} onChange={(e) => set("genericName", e.target.value)} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Drug class *</Label>
          <Input value={value.drugClass} onChange={(e) => set("drugClass", e.target.value)} placeholder="e.g. DMARD" />
        </div>
        <div>
          <Label>BNF reference</Label>
          <Input value={value.bnfReference ?? ""} onChange={(e) => set("bnfReference", e.target.value)} />
        </div>
      </div>
      <div>
        <Label>Indication *</Label>
        <Textarea rows={2} value={value.indication} onChange={(e) => set("indication", e.target.value)} />
      </div>
      <div>
        <Label>Monitoring requirements *</Label>
        <Textarea rows={2} value={value.monitoringRequirements} onChange={(e) => set("monitoringRequirements", e.target.value)} />
      </div>
      <div>
        <Label>Nurse notes</Label>
        <Textarea rows={2} value={value.nurseNotes ?? ""} onChange={(e) => set("nurseNotes", e.target.value)} />
      </div>
      <div>
        <Label>Patient notes</Label>
        <Textarea rows={2} value={value.patientNotes ?? ""} onChange={(e) => set("patientNotes", e.target.value)} />
      </div>
      <div>
        <Label>Contraindications (comma-separated)</Label>
        <Input value={arrStr(value.contraindications)} onChange={(e) => set("contraindications", strArr(e.target.value))} />
      </div>
      <div>
        <Label>Side effects (comma-separated)</Label>
        <Input value={arrStr(value.sideEffects)} onChange={(e) => set("sideEffects", strArr(e.target.value))} />
      </div>
      <div>
        <Label>Mechanism of action</Label>
        <Textarea rows={2} value={value.mechanism ?? ""} onChange={(e) => set("mechanism", e.target.value)} />
      </div>
      <div>
        <Label>BSR guidance</Label>
        <Textarea rows={2} value={value.bsrGuidance ?? ""} onChange={(e) => set("bsrGuidance", e.target.value)} />
      </div>
      <div>
        <Label>Patient summary</Label>
        <Textarea rows={2} value={value.patientSummary ?? ""} onChange={(e) => set("patientSummary", e.target.value)} />
      </div>
      <div>
        <Label>Patient medication guide</Label>
        <Textarea rows={3} value={value.patientMedicationGuide ?? ""} onChange={(e) => set("patientMedicationGuide", e.target.value)} />
      </div>
    </div>
  );
}

export default function AdminMedications() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [editing, setEditing] = useState<Medication | null>(null);
  const [form, setForm] = useState<MedForm>(EMPTY);

  const { data: meds = [], isLoading } = useQuery<Medication[]>({
    queryKey: ["admin", "medications"],
    queryFn: () => apiFetch("/api/admin/content/medications"),
  });

  const saveMutation = useMutation({
    mutationFn: (body: MedForm) =>
      editing
        ? apiFetch(`/api/admin/content/medications/${editing.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
        : apiFetch("/api/admin/content/medications", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "medications"] });
      setDialogOpen(false);
      toast({ title: editing ? "Medication updated" : "Medication created" });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiFetch(`/api/admin/content/medications/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "medications"] }); toast({ title: "Medication deleted" }); },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  function openCreate() { setEditing(null); setForm(EMPTY); setDialogOpen(true); }
  function openEdit(m: Medication) {
    setEditing(m);
    setForm({ name: m.name, genericName: m.genericName, drugClass: m.drugClass, indication: m.indication, mechanism: m.mechanism, nurseNotes: m.nurseNotes, patientNotes: m.patientNotes, monitoringRequirements: m.monitoringRequirements, contraindications: m.contraindications, sideEffects: m.sideEffects, bsrGuidance: m.bsrGuidance, pharmacokinetics: m.pharmacokinetics, pharmacodynamics: m.pharmacodynamics, bnfReference: m.bnfReference, patientSummary: m.patientSummary, patientMedicationGuide: m.patientMedicationGuide });
    setDialogOpen(true);
  }

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Medications"
        description={`${meds.length} medication${meds.length !== 1 ? "s" : ""}`}
        action={<Button size="sm" onClick={openCreate}><Plus className="h-4 w-4 mr-2" />New medication</Button>}
      />

      {isLoading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <AdminTable
          columns={["Name", "Drug class", "Indication", "Actions"]}
          rows={meds.map((m) => [
            <div key="name"><p className="font-medium text-slate-900">{m.name}</p><p className="text-xs text-slate-500">{m.genericName}</p></div>,
            <Badge key="class" variant="outline" className="text-xs">{m.drugClass}</Badge>,
            <span key="ind" className="text-xs text-slate-600 line-clamp-1">{m.indication}</span>,
            <div key="actions" className="flex items-center gap-2">
              <Button size="icon" variant="ghost" onClick={() => openEdit(m)}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => setDeleteId(m.id)}><Trash2 className="h-4 w-4" /></Button>
            </div>,
          ])}
        />
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Edit medication" : "New medication"}</DialogTitle></DialogHeader>
          <MedForm value={form} onChange={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={() => saveMutation.mutate(form)} disabled={saveMutation.isPending}>
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteId !== null} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete medication?</AlertDialogTitle><AlertDialogDescription>This will permanently remove the medication.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => { if (deleteId) { deleteMutation.mutate(deleteId); setDeleteId(null); } }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
